'use client';

import { Button, IcCloseCircle, IconButton, TextField, Typography } from '@musinsa/mds';
import type { GetNicknameRecommendationServiceResponse } from '@musinsa/services-curator';
import { getNicknameRecommendationService } from '@musinsa/services-curator';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useCuratorSnackbar } from '../../common/hooks';
import { useDebounceCallback } from '../../common/hooks/useDebounceCallback';
import { curatorQueries } from '../../query-factory';
import type { NicknameForm } from './NicknameEditForm';

// NOTE: 닉네임 유효성 검사 및 타이핑 상태 디바운스 지연 시간
export const NICKNAME_VALIDATION_DEBOUNCE_DELAY = 300;

interface Props {
  className?: string;
  hasError: boolean;
  disabled: boolean;
  onMutationReset: () => void;
}

export const NicknameRecommendationInput = ({
  className,
  hasError,
  disabled,
  onMutationReset,
}: Props) => {
  const snackbar = useCuratorSnackbar();
  const queryClient = useQueryClient();
  const { watch, setValue, setError, clearErrors } = useFormContext<NicknameForm>();
  const nickname = watch('nickname');
  const recommendedNicknameRef = useRef('');

  // Debounced 닉네임 업데이트 및 유효성 검사
  const debouncedSetNickname = useDebounceCallback(async (value: string) => {
    setValue('nickname', value, { shouldDirty: true });

    // 빈 문자열이거나 추천받은 닉네임이면 에러 클리어만 하고 API 호출 안 함
    if (!value.trim() || value === recommendedNicknameRef.current) {
      clearErrors('nickname');
      return;
    }

    // 유효성 검사 API 호출
    try {
      await queryClient.fetchQuery(curatorQueries.profile.nicknameValidation(value));
      clearErrors('nickname');
    } catch (error) {
      setError('nickname', {
        type: 'manual',
        message: (error as Error)?.message ?? '이미 사용 중인 닉네임입니다.',
      });
    }
  }, NICKNAME_VALIDATION_DEBOUNCE_DELAY);

  const { mutate: getNicknameRecommendation, isPending } = useMutation({
    mutationFn: getNicknameRecommendationService,
    onSuccess: (data: GetNicknameRecommendationServiceResponse) => {
      onMutationReset();
      setValue('nickname', data.nickname, { shouldDirty: true });
      clearErrors('nickname');
      recommendedNicknameRef.current = data.nickname;
    },
    onError: (error: Error) => {
      snackbar.error(error.message);
    },
  });

  const handleRecommendation = () => {
    getNicknameRecommendation();
  };

  const onChangeValue = (value: string) => {
    if (value === nickname) {
      return;
    }
    onMutationReset();
    setValue('nickname', value); // 사용자에게는 즉시 보여주기 위해 바로 업데이트

    // 빈 문자열이면 즉시 에러 클리어 처리
    if (value === '') {
      clearErrors('nickname');
    }

    debouncedSetNickname(value); // validate API호출을 위해 300ms 후에 몰아서 업데이트

    // 한글자라도 입력되면 recommendedNickname 초기화
    if (value !== recommendedNicknameRef.current) {
      recommendedNicknameRef.current = '';
    }
  };

  return (
    <div className={twMerge('flex space-x-1 items-center w-full', className)}>
      <div className="inline-flex relative w-full">
        <TextField
          fullWidth
          placeholder="닉네임 입력(최대 8자)"
          variant="outlined"
          value={nickname}
          onChange={(e) => onChangeValue(e.target.value)}
          maxLength={8}
          error={hasError}
          disabled={disabled}
        />
        <IconButton
          className="absolute right-2 top-2"
          size="size20x20"
          onClick={() => onChangeValue('')}
        >
          <IcCloseCircle fillColor="gray-400" />
        </IconButton>
      </div>

      <Button
        size={36}
        variant="secondary"
        onClick={handleRecommendation}
        disabled={isPending || disabled}
      >
        <Typography
          style={{
            textWrap: 'nowrap',
          }}
          variant="body13pxMed"
        >
          닉네임 추천받기
        </Typography>
      </Button>
    </div>
  );
};
